import React, { useEffect, useMemo, useState } from 'react';
import { NoItemsPanel, PageContentPreloader, PageLayout, SearchPanel } from 'common/components';
import { compose } from 'redux';
import noPaymentsImage from 'common/images/noPayments.svg';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { getBonusesInfoList, getBonusRecords } from '../../_redux';
import { RecordsBonusesTable } from '../../_components';
import { getUsers } from '../../../users/api';

const UserBonusesPage = ({
  profile,
  bonuses,
  bonusesAreLoaded,
  getBonusRecords,
  location,
  history,
  intl,
  bonusPrograms,
  getBonusesInfoList,
}) => {
  const [selectedUser, setSelectedUser] = useState({ value: '', label: '' });

  const query = {
    page: 1,
    page_size: 10,
    ...queryString.parse(location.search),
  };

  const bonusProgramOptions = useMemo(
    () =>
      Array.isArray(bonusPrograms)
        ? bonusPrograms.map(program => ({
            label: program.title,
            value: program.id,
          }))
        : [],
    [bonusPrograms]
  );

  const filters = [
    {
      title: 'justStatus',
      accessor: 'status',
      options: [
        { value: '', label: intl.formatMessage({ id: 'justAll' }) },
        { value: 'ACTIVE', label: intl.formatMessage({ id: 'status_active' }) },
        { value: 'COMPLETED', label: intl.formatMessage({ id: 'status_completed' }) },
        { value: 'REJECTED', label: intl.formatMessage({ id: 'status_rejected' }) },
        { value: 'EXPIRED', label: intl.formatMessage({ id: 'status_expired' }) },
        { value: 'CANCELED', label: intl.formatMessage({ id: 'status_canceled' }) },
        { value: 'AWAITING', label: intl.formatMessage({ id: 'status_awaiting' }) },
      ],
    },
    {
      title: 'boBonusTitle',
      accessor: 'bonus_id',
      options: [{ value: '', label: intl.formatMessage({ id: 'justAll' }) }, ...bonusProgramOptions],
    },
  ];

  const inputs = [
    {
      title: 'boAccountLogin',
      accessor: 'account__login',
    },
  ];

  const dateRangeField = [
    {
      title: 'boReceivingDate',
      queryStartDate: 'receiving_date__gte',
      queryEndDate: 'receiving_date__lte',
    },
    {
      title: 'boValidUntil',
      queryStartDate: 'valid_until__gte',
      queryEndDate: 'valid_until__lte',
      endlessPreset: true,
    },
    {
      title: 'boWithdrawDate',
      queryStartDate: 'withdraw_date__gte',
      queryEndDate: 'withdraw_date__lte',
    },
  ];

  const asyncSelectFields = [
    {
      title: 'justUser',
      accessor: 'account__user__id',
      loadOptions: search =>
        getUsers({ search }).then(({ data }) => data.map(user => ({ value: user.id, label: user.fullName }))),
      isMulti: false,
      defaultOptions: [selectedUser],
    },
  ];

  useEffect(() => {
    getBonusRecords({ search: query });
  }, []);

  useEffect(() => {
    getBonusesInfoList({ search: null });
  }, [getBonusesInfoList]);

  useEffect(() => {
    if (query.account__user__id) {
      getUsers({ search: query.account__user__id }).then(({ data }) => {
        if (data) {
          data
            .filter(user => Number(user.id) === Number(query.account__user__id))
            .map(user => setSelectedUser({ value: String(user.id), label: user.fullName }));
        }
      });
    }
  }, []);

  return (
    <PageLayout captionSlug="boUserBonuses" showReload={false}>
      <PageContentPreloader ready={bonusesAreLoaded} type="smallLines" firstLaunchOnly>
        <div className="UserBonuses__head">
          <SearchPanel
            location={location}
            history={history}
            getList={getBonusRecords}
            filters={filters}
            inputs={inputs}
            dateRangeField={dateRangeField}
            params={{ search: query }}
            asyncSelectFields={asyncSelectFields}
          />
        </div>
        {bonuses.count ? (
          <RecordsBonusesTable
            data={bonuses}
            loading={!bonusesAreLoaded}
            getList={getBonusRecords}
            location={location}
            history={history}
            query={query}
            params={{ search: query }}
            profile={profile}
          />
        ) : (
          <NoItemsPanel
            icon={noPaymentsImage}
            captionSlug={
              Object.keys(query).filter(obj => obj !== 'tab' && obj !== 'page_size').length
                ? 'noBonusesMatchingTheParameters'
                : 'noBonusesYet'
            }
          />
        )}
      </PageContentPreloader>
    </PageLayout>
  );
};
UserBonusesPage.propTypes = {
  profile: PropTypes.object.isRequired,
  getBonusRecords: PropTypes.func.isRequired,
  bonuses: PropTypes.object.isRequired,
  bonusesAreLoaded: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  bonusPrograms: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  getBonusesInfoList: PropTypes.func.isRequired,
};

UserBonusesPage.defaultProps = {};

export default compose(
  injectIntl,
  withRouter,
  connect(
    state => ({
      bonusPrograms: state.backoffice.bonuses.bonusesInfoList,
      profile: state.user.profile,
      bonuses: state.backoffice.bonuses.bonusRecords,
      bonusesAreLoaded: state.backoffice.bonuses.bonusRecordsAreLoaded,
    }),
    {
      getBonusRecords: search => getBonusRecords.request(search),
      getBonusesInfoList: data => getBonusesInfoList.request(data),
    }
  )
)(UserBonusesPage);
